import type { RefCallback } from 'react'

import {
  type Control,
  type FieldPath,
  type FieldValues,
  type PathValue,
  type UseFormRegister,
  type UseFormSetValue,
  useFormState,
  useWatch,
} from 'react-hook-form'

import type { IntradayFormulaFieldValues } from '~/pages/IntradayBidding/models/intradayFormula'

type FormMethods<T extends FieldValues> = {
  control: Control<T>
  register: UseFormRegister<T>
  setValue: UseFormSetValue<T>
}

export type IntradayFormulaFormBinding = {
  values: IntradayFormulaFieldValues
  onChange: (name: keyof IntradayFormulaFieldValues, value: string) => void
  offsetInputRef: RefCallback<HTMLInputElement>
}

/**
 * React Hook Form の form を、 フォーミュラ欄 (`IntradayFormulaInputs`) の `values` / `onChange` /
 * `offsetInputRef` に繋ぐ。 form は最上位に `formulaVariable` / `formulaReferenceArea` /
 * `formulaOperator` / `formulaOffset` の 4 欄 (string) を持つ (札作成・入札編集)。
 *
 * 値は setValue で入れ、 送信を一度押した後は欄を変えるたびに検証し直す (form の reValidateMode と
 * 同じ)。 オフセット値の欄は ref だけ登録し、 検証の失敗時の focus を保つ。
 */
export function useIntradayFormulaFormBinding<
  T extends FieldValues & IntradayFormulaFieldValues,
>({ control, register, setValue }: FormMethods<T>): IntradayFormulaFormBinding {
  // 4 欄の名前は form の型によらず同じ path (最上位の string 欄) なので、 form の path 型として扱う。
  const path = (name: keyof IntradayFormulaFieldValues): FieldPath<T> =>
    name as FieldPath<T>
  const [
    formulaVariable,
    formulaReferenceArea,
    formulaOperator,
    formulaOffset,
  ] = useWatch({
    control,
    name: [
      path('formulaVariable'),
      path('formulaReferenceArea'),
      path('formulaOperator'),
      path('formulaOffset'),
    ],
  }) as [string, string, string, string]
  const { isSubmitted } = useFormState({ control })
  return {
    values: {
      formulaVariable,
      formulaReferenceArea,
      formulaOperator,
      formulaOffset,
    },
    onChange: (name, value) => {
      setValue(path(name), value as PathValue<T, FieldPath<T>>, {
        shouldDirty: true,
        shouldValidate: isSubmitted,
      })
    },
    offsetInputRef: register(path('formulaOffset')).ref,
  }
}
